import { useState, useRef, useEffect } from "react";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import { getName } from "../profile.js";
import { submitPunchScore, fetchPunchBoard } from "../punchboard.js";

/* ==================== SLÅ SÆKKEN – sequential precision ====================
   Own score only (0-1000), no prizes. Four sequential phases, each locked
   with a single tap:  walk back (200) -> wind up the arm (200) ->
   power (300) -> aim (300). The mascot teddy is the boxer and FLIES into a
   hanging leather heavy bag; the bag's swing mirrors the punch power, the
   teddy's posture changes after every locked phase, and a total of 900+
   rips the bag off its chain, explodes it and sends the teddy celebrating.
   A finished score can be sent as a challenge link (?g=punch&ch=&by=).
============================================================================ */

const W = 360, H = 540;
const FLOOR = 442;
const PANEL_TOP = H - 78;                        // wooden HUD plank
/* hanging heavy bag: chains from the anchor, capsule body */
const BAG = { x: 262, anchorY: 4, top: 128, w: 86, h: 188 };
const IMPACT = { x: BAG.x - BAG.w / 2 - 12, y: 272 };   // glove meets leather here
const PHASES = ["walk", "windup", "power", "aim"];
const WEIGHTS = { walk: 200, windup: 200, power: 300, aim: 300 };
/* perfect-punkt, tolerance og TEMPO pr. fase (u er 0..1). Tempoet blev sat
   NED 14/7 ("næsten umuligt at ramme") — power/sigte er stadig strammest. */
const TUNE = {
  walk:   { perfect: 0.70, tol: 0.34, freq: 1.05 },
  windup: { perfect: 0.82, tol: 0.30, freq: 1.35 },
  power:  { perfect: 0.88, tol: 0.25, freq: 2.0 },
  aim:    { perfect: 0.50, tol: 0.22, freq: 1.6 },
};
const KO_SCORE = 900;                            // sækken eksploderer herfra
const BEST_KEY = "carnival.punch";

const loadBest = () => {
  try { return JSON.parse(localStorage.getItem(BEST_KEY) || "{}").best || 0; }
  catch { return 0; }
};
const saveBest = (best) => {
  try { localStorage.setItem(BEST_KEY, JSON.stringify({ best })); } catch {}
};

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    bell() { const c = ensure(); if (!c) return; [0, 0.18].forEach((d) => tone(1320, c.currentTime + d, 0.25, "triangle", 0.14)); },
    lock() { const c = ensure(); if (c) tone(760, c.currentTime, 0.07, "square", 0.09); },
    whoosh() {
      const c = ensure(); if (!c) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(160, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(680, c.currentTime + 0.18);
      g.gain.setValueAtTime(0.06, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.22);
      o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.24);
    },
    thud(power) {
      const c = ensure(); if (!c) return;
      tone(90 + power * 40, c.currentTime, 0.28, "square", 0.16 + power * 0.06);
      tone(55, c.currentTime + 0.02, 0.34, "sine", 0.14);
    },
    boom() {
      const c = ensure(); if (!c) return;
      tone(70, c.currentTime, 0.6, "sawtooth", 0.2);
      tone(48, c.currentTime + 0.03, 0.7, "square", 0.16);
      [900, 1300, 1750].forEach((f, i) => tone(f, c.currentTime + 0.05 + i * 0.05, 0.16, "triangle", 0.08));
    },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.12, 0.26, "triangle", 0.13));
    },
  };
}

/* score for at stoppe u i afstanden fra perfect (0..1 af fasens vægt) */
const zoneScore = (u, tune) => Math.max(0, 1 - Math.abs(u - tune.perfect) / tune.tol);

export default function Punch({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | result
  const [result, setResult] = useState(null);    // {scores:{...}, total, ko}
  const [best, setBest] = useState(loadBest);
  const [copied, setCopied] = useState(false);
  /* dobbelt high score (RTDB): overlay + faner (måned/all time) */
  const [hsOpen, setHsOpen] = useState(false);
  const [hsTab, setHsTab] = useState("month");
  const [hsData, setHsData] = useState(null);
  const [hsErr, setHsErr] = useState(false);
  const openHs = () => {
    setHsOpen(true);
    setHsErr(false);
    setHsData(null);
    fetchPunchBoard().then(setHsData).catch(() => setHsErr(true));
  };
  /* datoformat følger app-sproget (falder tilbage til browserens) */
  const fmtDate = (ts, withYear) => {
    let lang;
    try { lang = localStorage.getItem("carnival.lang") || undefined; } catch {}
    try {
      return new Date(ts).toLocaleDateString(lang, {
        day: "numeric", month: "short", ...(withYear ? { year: "2-digit" } : {}),
      });
    } catch { return ""; }
  };
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const gameRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  /* challenge-link: ?g=punch&ch=<score>&by=<navn> */
  const chal = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const ch = parseInt(q.get("ch"), 10);
      const by = (q.get("by") || "").slice(0, 12);
      return Number.isFinite(ch) && ch > 0 && ch <= 1000 && by ? { ch, by } : null;
    } catch { return null; }
  })();

  const startGame = () => {
    setResult(null);
    setCopied(false);
    setPhase("playing");
    audioRef.current.unlock();
    audioRef.current.bell();
  };

  const share = async (total) => {
    const url = `${window.location.origin}${window.location.pathname}?g=punch&ch=${total}&by=${encodeURIComponent(getName() || "?")}`;
    const text = t("pb_share_text", { n: total });
    try {
      if (navigator.share) { await navigator.share({ title: "Teddy Chase", text, url }); return; }
    } catch { /* deltes ikke — prøv clipboard */ }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      idx: 0,                 // hvilken fase (0-3)
      t: 0,                   // fasens egen tid
      u: 0,                   // fasens meter-position 0..1
      locked: {},             // {walk: u, ...} frosne positioner
      scores: {},             // {walk: point, ...}
      freeze: 0,              // pause m. popup mellem faser
      punchT: -1,             // >=0 = slag-sekvensen kører
      impactDone: false,
      total: 0,
      ko: false,              // 900+: sækken røg af kæden
      bagA: 0, bagW: 0,       // sækkens pendul (vinkel/vinkelfart)
      bagFly: null,           // {x,y,vx,vy,rot,vr} når kæden knækker
      exploded: false,
      parts: [],              // læderstumper/gnister/konfetti
      streaks: [],            // fartstriber bag flyvende bamse
      celebrateT: -1,
      popups: [],
      over: false,
      now: 0,
    };
    gameRef.current = g;
    if (localStorage.getItem("carnival.debug")) window.__px = g;

    /* ---- canvas sizing (fælles mønster) ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- sprites: 6 bokse-poser (genereres m. gen_plush_gemini.mjs --boxer);
       mangler de, falder vi tilbage til hangtime-spriten + tegnet handske ---- */
    const teddy = new Image();
    teddy.src = "/images/hangtime-teddy.png";
    const poses = {};
    for (const k of ["guard", "windup", "charge", "aim", "fly", "cheer"]) {
      const im = new Image();
      im.src = `/images/boxer/boxer_${k}.png`;
      poses[k] = im;
    }
    const poseReady = (k) => poses[k] && poses[k].complete && poses[k].naturalWidth > 0;
    /* hvilken pose passer til spillets tilstand? (kropsstilling pr. fase) */
    const currentPose = () => {
      if (g.punchT >= 0 && g.punchT < 0.3) return "fly";
      if (g.punchT >= 0.3) return g.ko ? "cheer" : "guard";
      const key = PHASES[g.idx];
      if (key === "walk") return "guard";
      if (key === "windup") return "windup";
      if (key === "power") return "charge";
      return "aim";
    };

    /* ---- statisk baggrund, bagt én gang (2x): telt + gardiner + planke ---- */
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);
      // mørkt teltindre m. striber
      const sky = b.createLinearGradient(0, 0, 0, PANEL_TOP);
      sky.addColorStop(0, "#3a1c30");
      sky.addColorStop(1, "#1e0f1a");
      b.fillStyle = sky; b.fillRect(0, 0, W, PANEL_TOP);
      for (let i = -2; i < 10; i++) {
        b.fillStyle = i % 2 ? "rgba(180,60,90,0.10)" : "rgba(240,230,210,0.05)";
        b.beginPath();
        b.moveTo(W / 2, -280);
        b.lineTo(i * 60, PANEL_TOP); b.lineTo(i * 60 + 60, PANEL_TOP);
        b.closePath(); b.fill();
      }
      // gardin-svaj i topsiderne
      b.fillStyle = "rgba(60,16,50,0.85)";
      b.beginPath();
      b.moveTo(0, 0); b.lineTo(92, 0);
      b.quadraticCurveTo(50, 44, 0, 66);
      b.closePath(); b.fill();
      b.beginPath();
      b.moveTo(W, 0); b.lineTo(W - 92, 0);
      b.quadraticCurveTo(W - 50, 44, W, 66);
      b.closePath(); b.fill();
      b.strokeStyle = "rgba(200,120,180,0.25)"; b.lineWidth = 2;
      b.beginPath(); b.moveTo(92, 0); b.quadraticCurveTo(50, 44, 0, 66); b.stroke();
      b.beginPath(); b.moveTo(W - 92, 0); b.quadraticCurveTo(W - 50, 44, W, 66); b.stroke();
      // spot bag sækken
      const sp = b.createRadialGradient(BAG.x - 20, 250, 30, BAG.x - 20, 250, 300);
      sp.addColorStop(0, "rgba(255,220,160,0.14)");
      sp.addColorStop(1, "rgba(0,0,0,0)");
      b.fillStyle = sp; b.fillRect(0, 0, W, H);
      // marquee-pærer øverst
      for (let i = 0; i < 12; i++) {
        const x = 18 + i * (W - 36) / 11;
        b.fillStyle = "rgba(255,225,150,0.9)";
        b.shadowColor = "#ffd75e"; b.shadowBlur = 7;
        b.beginPath(); b.arc(x, 12, 2.6, 0, Math.PI * 2); b.fill();
        b.shadowBlur = 0;
      }
      // gulvstribe + træpanel til meteret (som mockuppets planke)
      const fl = b.createLinearGradient(0, FLOOR, 0, PANEL_TOP);
      fl.addColorStop(0, "#5a3a22"); fl.addColorStop(1, "#42280f");
      b.fillStyle = fl; b.fillRect(0, FLOOR, W, PANEL_TOP - FLOOR);
      b.fillStyle = "rgba(255,235,200,0.22)"; b.fillRect(0, FLOOR, W, 2);
      const pl = b.createLinearGradient(0, PANEL_TOP, 0, H);
      pl.addColorStop(0, "#7a4e2c"); pl.addColorStop(0.5, "#5f3d1f"); pl.addColorStop(1, "#3f2812");
      b.fillStyle = pl; b.fillRect(0, PANEL_TOP, W, H - PANEL_TOP);
      b.strokeStyle = "rgba(30,15,5,0.45)"; b.lineWidth = 1.5;
      for (let i = 1; i < 4; i++) {
        b.beginPath(); b.moveTo(0, PANEL_TOP + i * 20); b.lineTo(W, PANEL_TOP + i * 20 - 3); b.stroke();
      }
      b.fillStyle = "#c9a24b"; b.fillRect(0, PANEL_TOP, W, 2.5);
      // vignette
      const vg = b.createRadialGradient(W / 2, H * 0.45, 120, W / 2, H * 0.5, 430);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(12,4,10,0.45)");
      b.fillStyle = vg; b.fillRect(0, 0, W, H);
    }

    /* ---- input: ét tap låser den aktive fase ---- */
    const lock = (e) => {
      audioRef.current.unlock();
      e.preventDefault();
      if (g.over || g.freeze > 0 || g.punchT >= 0) return;
      const key = PHASES[g.idx];
      g.locked[key] = g.u;
      const s = Math.round(zoneScore(g.u, TUNE[key]) * WEIGHTS[key]);
      g.scores[key] = s;
      audioRef.current.lock();
      g.popups.push({ txt: "+" + s, x: W / 2 - 40, y: 200, t: 0, c: s > WEIGHTS[key] * 0.8 ? "#ffd75e" : "#F6EED9" });
      if (g.idx < PHASES.length - 1) {
        g.idx += 1;
        g.t = 0;
        g.freeze = 0.55;
      } else {
        g.total = PHASES.reduce((sum, k) => sum + (g.scores[k] || 0), 0);
        g.ko = g.total >= KO_SCORE;
        g.punchT = 0;             // alle fire låst: FLYV!
        audioRef.current.whoosh();
      }
    };
    canvas.addEventListener("mousedown", lock);
    canvas.addEventListener("touchstart", lock, { passive: false });

    const finish = () => {
      if (g.over) return;
      g.over = true;
      audioRef.current.win();
      const total = g.total;
      submitPunchScore(total);          // alle scores tæller (fire-and-forget)
      setTimeout(() => {
        setResult({ scores: { ...g.scores }, total, ko: g.ko });
        setBest((b2) => {
          const nb = Math.max(b2, total);
          if (nb !== b2) saveBest(nb);
          return nb;
        });
        setPhase("result");
      }, 700);
    };

    /* hvor står bamsen (center-x) før slaget? Spændet er klemt, så den brede
       pose-figur (158px høj, ~halv bredde 79) aldrig klipper i venstre kant */
    const WALK_NEAR = 168, WALK_SPAN = 82;
    const standX = () => {
      const u = g.locked.walk !== undefined ? g.locked.walk : (PHASES[g.idx] === "walk" ? g.u : 0.5);
      return WALK_NEAR - u * WALK_SPAN;
    };

    /* ---- step ---- */
    const step = (dt) => {
      g.now += dt;
      if (g.freeze > 0) g.freeze -= dt;
      else if (g.punchT < 0 && !g.over) {
        g.t += dt;
        const key = PHASES[g.idx];
        g.u = 0.5 + 0.5 * Math.sin(g.t * TUNE[key].freq * Math.PI - Math.PI / 2);
      }

      // sækkens pendul om kædeophænget
      if (!g.bagFly) {
        g.bagW += (-26 * Math.sin(g.bagA) - 1.6 * g.bagW) * dt;
        g.bagA += g.bagW * dt;
      } else if (!g.exploded) {
        g.bagFly.x += g.bagFly.vx * dt;
        g.bagFly.y += g.bagFly.vy * dt;
        g.bagFly.vy += 500 * dt;
        g.bagFly.rot += g.bagFly.vr * dt;
        if (g.punchT > 0.72) {          // BOOM (mens sækken stadig er i billedet)
          g.exploded = true;
          audioRef.current.boom();
          const cx = g.bagFly.x, cy = g.bagFly.y + BAG.h / 2;
          for (let i = 0; i < 30; i++) {
            const a = Math.random() * Math.PI * 2, sp = 120 + Math.random() * 260;
            g.parts.push({
              x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 80,
              rot: Math.random() * 6, vr: (Math.random() - 0.5) * 10,
              type: i % 3 ? "leather" : "spark", t: 0, life: 1 + Math.random() * 0.5,
            });
          }
          g.celebrateT = 0;
        }
      }

      // slag-sekvensen
      if (g.punchT >= 0 && !g.over) {
        g.punchT += dt;
        // fartstriber mens bamsen flyver
        if (g.punchT < 0.3 && Math.random() < 0.7) {
          const p = flightPos(Math.min(g.punchT / 0.3, 1));
          g.streaks.push({ x: p.x - 30, y: p.y + (Math.random() - 0.5) * 40, t: 0 });
        }
        if (g.punchT >= 0.3 && !g.impactDone) {
          g.impactDone = true;
          const powerFrac = (g.scores.power || 0) / WEIGHTS.power;
          audioRef.current.thud(powerFrac);
          if (g.ko) {
            // langsomt nok til at eksplosionen sker PÅ skærmen
            g.bagFly = { x: BAG.x + 6, y: BAG.top, vx: 110 + powerFrac * 50, vy: -160, rot: 0, vr: 2.6 };
          } else {
            g.bagW = 1.1 + (g.total / 1000) * 2.4;   // sving ∝ hvor hårdt der blev ramt
          }
        }
        if (g.celebrateT >= 0) g.celebrateT += dt;
        // konfetti under jubel
        if (g.celebrateT >= 0 && g.celebrateT < 1.6 && Math.random() < 0.5) {
          g.parts.push({
            x: Math.random() * W, y: -8, vx: (Math.random() - 0.5) * 40, vy: 60 + Math.random() * 80,
            rot: Math.random() * 6, vr: (Math.random() - 0.5) * 8,
            type: "confetti", t: 0, life: 2.2,
          });
        }
        if (!g.ko && g.punchT > 1.5) finish();
        if (g.ko && g.punchT > 2.6) finish();
      }

      g.parts.forEach((p) => {
        p.t += dt;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vy += (p.type === "confetti" ? 60 : 320) * dt;
        p.rot += p.vr * dt;
      });
      g.parts = g.parts.filter((p) => p.t < p.life && p.y < H + 20);
      g.streaks.forEach((s) => (s.t += dt));
      g.streaks = g.streaks.filter((s) => s.t < 0.35);
      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1);
    };

    /* bamsens position under flyveturen (k 0..1) */
    const flightPos = (k) => {
      const x0 = standX(), y0 = FLOOR - 70;
      const x1 = IMPACT.x - 26, y1 = IMPACT.y;
      const e = k * k * (3 - 2 * k);   // smoothstep
      return { x: x0 + (x1 - x0) * e, y: y0 + (y1 - y0) * e - Math.sin(k * Math.PI) * 26 };
    };

    /* ---- draw helpers ---- */
    const rr = (c, x, y, w2, h2, r) => {
      c.beginPath();
      c.moveTo(x + r, y);
      c.arcTo(x + w2, y, x + w2, y + h2, r);
      c.arcTo(x + w2, y + h2, x, y + h2, r);
      c.arcTo(x, y + h2, x, y, r);
      c.arcTo(x, y, x + w2, y, r);
      c.closePath();
    };

    const drawChain = (x0, y0, x1, y1) => {
      const n = Math.max(3, Math.round(Math.hypot(x1 - x0, y1 - y0) / 11));
      for (let i = 0; i <= n; i++) {
        const k = i / n;
        const x = x0 + (x1 - x0) * k, y = y0 + (y1 - y0) * k;
        ctx.strokeStyle = i % 2 ? "#b9c0c9" : "#7e868e";
        ctx.lineWidth = 2.4;
        ctx.beginPath(); ctx.ellipse(x, y, 3.2, 5, Math.atan2(y1 - y0, x1 - x0) + Math.PI / 2, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.lineWidth = 1;
    };

    /* selve sækken (lokal 0,0 = ophængspunktet), genbruges af pendul + flyvetur */
    const drawBagBody = (topY) => {
      const wH = BAG.w / 2;
      // kæder ned til sækkens to øjer
      drawChain(0, 2, -wH + 14, topY);
      drawChain(0, 2, wH - 14, topY);
      drawChain(0, 2, 0, topY - 4);
      // læder-kapsel
      const grad = ctx.createLinearGradient(-wH, 0, wH, 0);
      grad.addColorStop(0, "#3a2010");
      grad.addColorStop(0.35, "#6b3f1e");
      grad.addColorStop(0.6, "#7a4a24");
      grad.addColorStop(1, "#2c180a");
      rr(ctx, -wH, topY, BAG.w, BAG.h, 30);
      ctx.fillStyle = grad; ctx.fill();
      ctx.strokeStyle = "rgba(20,8,2,0.7)"; ctx.lineWidth = 2; ctx.stroke();
      // topkrave + syninger
      ctx.strokeStyle = "rgba(240,214,122,0.4)";
      ctx.lineWidth = 1.4;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(-wH + 6, topY + 20); ctx.lineTo(wH - 6, topY + 20); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-wH + 6, topY + BAG.h - 18); ctx.lineTo(wH - 6, topY + BAG.h - 18); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, topY + 22); ctx.lineTo(0, topY + BAG.h - 20); ctx.stroke();
      ctx.setLineDash([]);
      // kryds-lap som mockuppet
      ctx.strokeStyle = "rgba(30,14,4,0.8)"; ctx.lineWidth = 2;
      ctx.strokeRect(-16, topY + 34, 32, 30);
      ctx.beginPath();
      ctx.moveTo(-16, topY + 34); ctx.lineTo(16, topY + 64);
      ctx.moveTo(16, topY + 34); ctx.lineTo(-16, topY + 64);
      ctx.stroke();
      // blank højlys-stribe
      const sheen = ctx.createLinearGradient(-wH, 0, 0, 0);
      sheen.addColorStop(0, "rgba(255,235,200,0)");
      sheen.addColorStop(0.7, "rgba(255,235,200,0.13)");
      sheen.addColorStop(1, "rgba(255,235,200,0)");
      rr(ctx, -wH, topY, BAG.w, BAG.h, 30);
      ctx.fillStyle = sheen; ctx.fill();
      ctx.lineWidth = 1;
    };

    const drawBag = () => {
      if (g.exploded) return;
      if (g.bagFly) {
        ctx.save();
        ctx.translate(g.bagFly.x, g.bagFly.y);
        ctx.rotate(g.bagFly.rot);
        drawBagBody(0);
        ctx.restore();
        return;
      }
      ctx.save();
      ctx.translate(BAG.x, BAG.anchorY);
      ctx.rotate(g.bagA);
      drawBagBody(BAG.top);
      ctx.restore();
    };

    /* bamsen: kropsstilling pr. fase via pose-renders (guard/antræk/charge/
       sigte/flyv/jubel); statur-hældning lægges ovenpå som ekstra krydderi */
    const drawTeddy = () => {
      const pose = currentPose();
      const hasPose = poseReady(pose);
      // pose-renders er større og mere detaljerede end fallback-spriten
      const th = hasPose ? 158 : 118;
      let cx, cy, rot = 0;
      const flying = g.punchT >= 0 && g.punchT < 0.3;
      const afterPunch = g.punchT >= 0.3;
      if (flying) {
        const k = Math.min(g.punchT / 0.3, 1);
        const p = flightPos(k);
        cx = p.x; cy = p.y;
        rot = hasPose ? 0.15 * k : 0.9 * k;  // fly-posen ER vandret; fallback vippes
      } else if (afterPunch) {
        if (g.ko) {
          // jubel: hop på stedet foran sækkens gamle plads
          const jump = g.celebrateT >= 0 ? Math.abs(Math.sin(g.celebrateT * 5.2)) * 26 : 0;
          cx = 170; cy = FLOOR - th / 2 - jump;
          rot = Math.sin((g.celebrateT || 0) * 10) * 0.08;
        } else {
          // land tilbage på gulvet
          const k = Math.min((g.punchT - 0.3) / 0.4, 1);
          const p0 = flightPos(1);
          cx = p0.x + (standX() - p0.x) * k;
          cy = p0.y + (FLOOR - th / 2 - p0.y) * k - Math.sin(k * Math.PI) * 20;
          rot = (hasPose ? 0.15 : 0.9) * (1 - k);
        }
      } else {
        cx = standX();
        cy = FLOOR - th / 2 + (g.punchT < 0 ? Math.sin(g.now * 3.2) * 2 : 0);
        // let ekstra hældning oven på posen: tilbage m. antræk, frem m. power
        if (g.locked.windup !== undefined && g.locked.power === undefined) rot -= 0.06;
        if (g.locked.power !== undefined) rot += 0.10;
      }

      // power-aura når kræfterne er fundet frem
      const powerFrac = (g.scores.power || 0) / WEIGHTS.power;
      if (g.locked.power !== undefined && !g.exploded && powerFrac > 0.25) {
        const au = ctx.createRadialGradient(cx, cy, 8, cx, cy, 72);
        au.addColorStop(0, `rgba(255,205,90,${0.20 * powerFrac})`);
        au.addColorStop(1, "rgba(255,205,90,0)");
        ctx.fillStyle = au;
        ctx.beginPath(); ctx.arc(cx, cy, 72, 0, Math.PI * 2); ctx.fill();
      }

      // fartstriber
      for (const s of g.streaks) {
        ctx.globalAlpha = Math.max(0, 1 - s.t / 0.35) * 0.6;
        ctx.strokeStyle = "#ffce7a"; ctx.lineWidth = 3; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(s.x - 26, s.y); ctx.lineTo(s.x + 8, s.y); ctx.stroke();
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      if (hasPose) {
        // handskerne er MED i pose-renderen — ingen tegnet arm/handske
        const im = poses[pose];
        const tw = th * (im.naturalWidth / im.naturalHeight);
        ctx.drawImage(im, -tw / 2, -th / 2, tw, th);
      } else if (teddy.complete && teddy.naturalWidth) {
        const tw = 100;
        ctx.drawImage(teddy, -tw / 2, -th / 2, tw, th);
        // fallback: tegnet boksehandske (indtil --boxer-renders er genereret)
        let gx = 26, gy = -18;
        if (!flying && !afterPunch) {
          const key = PHASES[g.idx];
          const wu = g.locked.windup !== undefined ? g.locked.windup
            : (key === "windup" ? g.u : -1);
          if (wu >= 0) { gx = 24 - wu * 34; gy = 4 + wu * 16; }   // trækkes nedad/bagud
        } else {
          gx = 40; gy = -46;                      // strakt frem foran hovedet
        }
        const gg = ctx.createRadialGradient(gx - 3, gy - 4, 2, gx, gy, 13);
        gg.addColorStop(0, "#e5563f"); gg.addColorStop(1, "#8a1811");
        ctx.strokeStyle = "#8a5a33"; ctx.lineWidth = 8; ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(16, -12); ctx.lineTo(gx - 4, gy + 4); ctx.stroke();
        ctx.beginPath(); ctx.arc(gx, gy, 12, 0, Math.PI * 2);
        ctx.fillStyle = gg; ctx.fill();
        ctx.strokeStyle = "#f0d67a"; ctx.lineWidth = 1.5; ctx.stroke();
      } else {
        ctx.fillStyle = "#a97b4f";
        ctx.beginPath(); ctx.arc(0, -28, 24, 0, Math.PI * 2); ctx.fill();
        rr(ctx, -20, -8, 40, 58, 14); ctx.fill();
      }
      ctx.restore();
      ctx.lineWidth = 1;
    };

    const drawImpact = () => {
      if (!g.impactDone || g.punchT > 0.75) return;
      const k = Math.max(0, 1 - (g.punchT - 0.3) / 0.45);
      const ix = IMPACT.x + 12, iy = IMPACT.y;
      // stjerne-flash
      ctx.globalAlpha = k;
      ctx.fillStyle = "#fff3c8";
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        const r = i % 2 ? 12 : 30 + (g.total / 1000) * 16;
        ctx[i ? "lineTo" : "moveTo"](ix + Math.cos(a) * r, iy + Math.sin(a) * r);
      }
      ctx.closePath(); ctx.fill();
      // ekspanderende guldring (mockuppets ring)
      const ringR = 20 + (1 - k) * 70;
      ctx.strokeStyle = "#ffd75e"; ctx.lineWidth = 3.5;
      ctx.beginPath(); ctx.arc(ix, iy, ringR, 0, Math.PI * 2); ctx.stroke();
      ctx.globalAlpha = 1; ctx.lineWidth = 1;
    };

    const drawParts = () => {
      for (const p of g.parts) {
        const a = Math.max(0, 1 - p.t / p.life);
        ctx.globalAlpha = a;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.type === "leather") {
          ctx.fillStyle = "#5f3a1c";
          rr(ctx, -6, -4, 12, 8, 3); ctx.fill();
        } else if (p.type === "spark") {
          ctx.fillStyle = "#ffd75e";
          ctx.shadowColor = "#ffd75e"; ctx.shadowBlur = 8;
          ctx.beginPath(); ctx.arc(0, 0, 3, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = ["#e5563f", "#ffd75e", "#5fc0e8", "#7acb5f"][Math.floor(p.rot * 7) % 4];
          ctx.fillRect(-4, -2, 8, 4);
        }
        ctx.restore();
        ctx.globalAlpha = 1;
      }
    };

    const drawMeter = () => {
      if (g.punchT >= 0 || g.over) return;
      const key = PHASES[g.idx];
      const tune = TUNE[key];
      const mx = 26, mw = W - 52, my = H - 40;
      // guldmeter i træpanelet (mockup-stil)
      ctx.fillStyle = "rgba(30,15,5,0.65)";
      rr(ctx, mx - 3, my - 3, mw + 6, 22, 11); ctx.fill();
      ctx.strokeStyle = "#c9a24b"; ctx.lineWidth = 2;
      rr(ctx, mx - 3, my - 3, mw + 6, 22, 11); ctx.stroke();
      const zx = mx + (tune.perfect - tune.tol) * mw;
      const zw = tune.tol * 2 * mw;
      ctx.fillStyle = "rgba(255,215,94,0.30)";
      rr(ctx, Math.max(mx, zx), my, Math.min(zw, mx + mw - zx), 16, 8); ctx.fill();
      const fillW = Math.max(10, g.u * mw);
      const fg = ctx.createLinearGradient(0, my, 0, my + 16);
      fg.addColorStop(0, "#f7df9a"); fg.addColorStop(1, "#c98f2a");
      rr(ctx, mx, my, fillW, 16, 8); ctx.fillStyle = fg; ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.fillRect(mx + tune.perfect * mw - 1.5, my - 6, 3, 28);
      const vx = mx + g.u * mw;
      ctx.beginPath();
      ctx.moveTo(vx - 7, my - 10); ctx.lineTo(vx + 7, my - 10); ctx.lineTo(vx, my - 1);
      ctx.closePath(); ctx.fill();
      // fase-instruktion
      ctx.textAlign = "center";
      ctx.font = "600 14px Georgia, 'Times New Roman', serif";
      ctx.lineWidth = 3; ctx.strokeStyle = "rgba(20,8,14,0.7)";
      const txt = t("pb_hint_" + key);
      ctx.strokeText(txt, W / 2, my - 14, W - 40);
      ctx.fillStyle = "#e7c469";
      ctx.fillText(txt, W / 2, my - 14, W - 40);
      ctx.lineWidth = 1;
      ctx.font = "800 12px system-ui, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText((g.idx + 1) + " / 4", W / 2, my + 30);
    };

    const draw = () => {
      ctx.drawImage(bg, 0, 0, W, H);
      // walk-fasens perfekt-markering på gulvet
      if (PHASES[g.idx] === "walk" && g.locked.walk === undefined && g.punchT < 0) {
        const px0 = WALK_NEAR - TUNE.walk.perfect * WALK_SPAN;
        ctx.strokeStyle = "rgba(255,215,94,0.8)"; ctx.lineWidth = 3;
        ctx.setLineDash([6, 5]);
        ctx.beginPath(); ctx.ellipse(px0, FLOOR - 2, 30, 8, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]); ctx.lineWidth = 1;
      }
      drawBag();
      drawTeddy();
      drawImpact();
      drawParts();
      // fase-chips (kobber/træ som mockuppet)
      ctx.textAlign = "left";
      PHASES.forEach((k, i) => {
        const cxp = 10 + i * 86;
        const done = g.scores[k] !== undefined;
        const cg = ctx.createLinearGradient(0, 24, 0, 52);
        cg.addColorStop(0, done ? "#8a5a33" : "#4a2f1c");
        cg.addColorStop(1, done ? "#5f3d1f" : "#33200f");
        rr(ctx, cxp, 24, 80, 28, 9);
        ctx.fillStyle = cg; ctx.fill();
        ctx.strokeStyle = done ? "#f0d67a" : "rgba(201,162,75,0.4)"; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.font = "800 10px system-ui, sans-serif";
        ctx.fillStyle = done ? "#ffe9a8" : "rgba(246,238,217,0.6)";
        ctx.fillText(t("pb_ph_" + k), cxp + 8, 36);
        ctx.font = "800 12px system-ui, sans-serif";
        ctx.fillStyle = done ? "#ffd75e" : "rgba(255,255,255,0.35)";
        ctx.fillText(done ? g.scores[k] + "" : "· · ·", cxp + 8, 48);
        ctx.lineWidth = 1;
      });
      drawMeter();
      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = 1 - p.t;
        ctx.font = "bold 26px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(20,8,14,0.8)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 30);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 30);
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (ts) => {
      const dt = Math.min((ts - last) / 1000, 0.033);
      last = ts;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", lock);
      canvas.removeEventListener("touchstart", lock);
    };
  }, [phase]);

  /* ================= UI ================= */
  const beatChallenge = result && chal && result.total > chal.ch;

  return (
    <div className={"px-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_punch").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("pb_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          {chal && (
            <div className="px-chal">{t("pb_ch_banner", { name: chal.by, n: chal.ch })}</div>
          )}
          <p className="lede">{t("pb_lede")}</p>
          <ul className="rules">
            <li>{t("pb_rule1")}</li>
            <li>{t("pb_rule2")}</li>
            <li>{t("pb_rule3")}</li>
            <li>{t("pb_rule4")}</li>
          </ul>
          <p className="prizenote">{t("pb_prizenote")}{best > 0 ? " · " + t("pb_best", { n: best }) : ""}</p>
          <button className="cta" onClick={startGame}>{t("pb_start")}</button>
          <button className="cta hs" onClick={openHs}>{t("pb_hs_btn")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("pb_best", { n: best })}</span>
            {chal && <span className="last">{chal.by}: {chal.ch}</span>}
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "result" && result && (
        <div className="overlay">
          <div className="card">
            <div className={"ribbon" + (result.total >= 800 ? "" : " dark")}>
              {result.ko ? t("pb_res_ko") : result.total >= 800 ? t("pb_res_great") : result.total >= 450 ? t("pb_res_ok") : t("pb_res_low")}
            </div>
            <p className="px-total">{result.total}<span> / 1000</span></p>
            <div className="px-break">
              {PHASES.map((k) => (
                <div key={k} className="px-row">
                  <span>{t("pb_ph_" + k)}</span>
                  <b>{result.scores[k] || 0} / {WEIGHTS[k]}</b>
                </div>
              ))}
            </div>
            {chal && (
              <p className="px-chresult">
                {beatChallenge ? t("pb_ch_beat", { name: chal.by }) : t("pb_ch_lost", { name: chal.by, n: chal.ch })}
              </p>
            )}
            <button className="cta gold" onClick={() => share(result.total)}>
              {copied ? t("pb_copied") : t("pb_challenge")}
            </button>
            <button className="cta hs" onClick={openHs}>{t("pb_hs_btn")}</button>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {hsOpen && (() => {
        const list = hsData ? (hsTab === "month" ? hsData.month : hsData.alltime) : [];
        return (
          <div className="overlay hsov" onClick={(e) => { if (e.target === e.currentTarget) setHsOpen(false); }}>
            <div className="card hscard">
              <div className="ribbon">{t("pb_hs_btn")}</div>
              <div className="hstabs">
                <button className={hsTab === "month" ? "on" : ""} onClick={() => setHsTab("month")}>{t("pb_hs_month")}</button>
                <button className={hsTab === "all" ? "on" : ""} onClick={() => setHsTab("all")}>{t("pb_hs_all")}</button>
              </div>
              {!hsData && !hsErr && <p className="hsmsg">· · ·</p>}
              {hsErr && <p className="hsmsg">{t("pb_hs_err")}</p>}
              {hsData && (list.length ? (
                <div className="hslist">
                  {list.map((e, i) => (
                    <div key={e.dev + i} className={"hsrow" + (e.me ? " me" : "")}>
                      <span className="rk">{i + 1}</span>
                      <span className="nm">{e.name}<em>{fmtDate(e.ts, hsTab === "all")}</em></span>
                      <b>{e.score}</b>
                    </div>
                  ))}
                </div>
              ) : <p className="hsmsg">{t("pb_hs_empty")}</p>)}
              <button className="cta alt" onClick={() => setHsOpen(false)}>{t("pb_hs_close")}</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const css = `
.px-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#1c0e18;
  background-image:radial-gradient(circle at 50% -10%, #4a2438 0%, #1c0e18 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.px-chal{
  background:linear-gradient(#f7df9a,#e9b94e); color:#4a2c00;
  border:2px solid #a87914; border-radius:10px;
  font-weight:800; font-size:14px; padding:10px 12px; margin:0 0 12px;
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.cta{
  width:100%; margin-top:14px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.cta.alt{margin-top:10px; background:#6b4a5a; box-shadow:0 4px 0 #3f2a36; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #3f2a36;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #5a3520; box-shadow:0 12px 30px rgba(0,0,0,.55);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(14,6,12,.78); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#4a3520;}
.px-total{
  font-family:'Alfa Slab One',Georgia,serif; font-size:46px; margin:2px 0 10px;
  color:#7e1f1a;
}
.px-total span{font-size:18px; color:#8a7a55;}
.px-break{margin:0 0 12px;}
.px-row{
  display:flex; justify-content:space-between; font-weight:800; font-size:13.5px;
  padding:5px 2px; border-bottom:1px dashed #cbbf9c;
}
.px-row:last-child{border-bottom:none;}
.px-chresult{font-weight:800; font-size:14px; margin:0 0 4px; color:#7e1f1a;}
.cta.hs{
  background:linear-gradient(180deg,#7a4e2c,#5f3d1f); color:#ffe9a8;
  border:1.5px solid #c9a24b; box-shadow:0 4px 0 #3f2812; font-size:15px; padding:11px;
}
.cta.hs:active{box-shadow:0 1px 0 #3f2812;}
.hsov{z-index:12;}
.hscard{max-height:86vh; overflow-y:auto;}
.hstabs{display:flex; gap:8px; margin:0 0 10px;}
.hstabs button{
  flex:1; padding:9px 6px; font-family:inherit; font-weight:800; font-size:13.5px;
  border-radius:8px; cursor:pointer; border:1.5px solid #cbbf9c; background:#efe6cd; color:#6b5c38;
}
.hstabs button.on{background:linear-gradient(180deg,#f7df9a,#e9b94e); border-color:#a87914; color:#4a2c00;}
.hslist{margin:0 0 6px;}
.hsrow{
  display:flex; align-items:center; gap:10px; font-weight:800; font-size:14px;
  padding:6px 8px; border-bottom:1px dashed #cbbf9c; border-radius:6px;
}
.hsrow:last-child{border-bottom:none;}
.hsrow .rk{width:20px; text-align:right; color:#8a7a55;}
.hsrow .nm{flex:1; text-align:left; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.hsrow .nm em{font-style:normal; font-weight:700; font-size:11px; color:#8a7a55; margin-left:8px;}
.hsrow b{color:#7e1f1a; font-size:15px;}
.hsrow.me{background:linear-gradient(180deg,#f9ecc4,#f2dc9a); box-shadow:inset 0 0 0 1.5px #c9a24b;}
.hsmsg{font-weight:800; font-size:14px; color:#8a7a55; padding:14px 0;}
`;
